import { logger } from "@/lib/logger";

export type ProfileSaveInput = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureUrl: string;
};

/**
 * How long an identical save stays deduplicated after it resolved. The duplicate we are
 * suppressing lands within a second of the first one, so this only has to outlive a remount.
 */
const RECENT_SAVE_TTL_MS = 10_000;

type SaveRecord = { promise: Promise<unknown>; settledAt: number | null };

/**
 * Module level on purpose: the duplicate comes from `SignInView` remounting, which resets
 * every ref and piece of component state, so an in-component guard cannot see it (LAR-436).
 */
const saves = new Map<string, SaveRecord>();

const signature = (input: ProfileSaveInput) =>
  [
    input.id,
    input.firstName,
    input.lastName,
    input.email,
    input.profilePictureUrl,
  ].join("|");

const isUsable = (record: SaveRecord, now: number) =>
  record.settledAt === null || now - record.settledAt < RECENT_SAVE_TTL_MS;

/**
 * Single-flights the onboarding profile save.
 *
 * Nearly every onboarding sent `PUT /api/user/{id}` twice, 0-1 s apart, and the two writes
 * raced on the same Marten event stream - the loser came back as a 500 with a duplicate-key
 * violation (LAR-436). Identical payloads share one request, and a save that has just
 * succeeded is reused for RECENT_SAVE_TTL_MS, since replaying it would not change server
 * state. Anything the user actually changed has a different signature and always goes
 * through, and a failed save is forgotten immediately so a retry is a real request.
 */
export function saveProfileOnce<T>(
  input: ProfileSaveInput,
  save: (input: ProfileSaveInput) => Promise<T>
): Promise<T> {
  const key = signature(input);
  const existing = saves.get(key);

  if (existing && isUsable(existing, Date.now())) {
    logger.info("Reusing in-flight profile save", {
      operation: "saveProfileOnce",
      userId: input.id,
      alreadySettled: existing.settledAt !== null,
    });
    return existing.promise as Promise<T>;
  }

  const promise = save(input).then(
    (value) => {
      const record = saves.get(key);
      if (record) record.settledAt = Date.now();
      return value;
    },
    (error) => {
      saves.delete(key);
      throw error;
    }
  );

  saves.set(key, { promise, settledAt: null });
  return promise;
}

/** Test seam: drops the dedupe memory so cases do not leak into each other. */
export function resetProfileSaveCacheForTests() {
  saves.clear();
}
